import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useT } from '../../i18n';
import { detectFloorPlan, isRoboflowConfigured } from '../../utils/roboflowService';
import { analysePlan, WALL_THICKNESS } from '../../utils/floorPlanVectors';
import { extractWalls, loadImage } from '../../utils/wallExtract';
import './FloorPlanImport.css';

// ── FLOOR PLAN IMPORT ────────────────────────────────────────────────────────
// Upload a 2D floor plan, read it, and show what was understood before the
// project is created.
//
// The preview is 2D — a plan view of the detected rooms, walls and openings,
// drawn the way the Builder draws them. That is the same view the user lands in
// after creating the project, so the preview shows what they are about to get
// rather than a separate rendering of it. It is also plain SVG: no WebGL
// context inside a modal, and nothing on the GPU to leak.
//
// The component owns the UPLOAD and the PREVIEW. It does not create anything —
// it reports {file, detection, scaleFactor, imageWalls} upward through
// `onChange`, and the modal decides what to do with it on submit.

// Metres per pixel. The default suits a plan a few hundred pixels wide; the
// slider exists because no drawing carries its scale in a form we can read.
const SCALE_MIN  = 0.005;
const SCALE_MAX  = 0.08;
const SCALE_STEP = 0.001;
const SCALE_DEFAULT = 0.02;

const PAD = 0.6;      // metres of margin around the drawing

// Overlay opacity, 0–1. 30% shows the drawing clearly enough to spot a
// misalignment while leaving the detected walls legible on top of it — the
// point being to refine the walls against the original, not to admire it.
const OVERLAY_DEFAULT = 0.3;

/**
 * 2D plan view of an analysed floor plan.
 *
 * Everything is in metres and the SVG viewBox does the fitting, so no manual
 * scaling maths is needed and the drawing stays crisp at any size.
 *
 * ══ THE IMAGE OVERLAY ═══════════════════════════════════════════════════════
 *
 * The uploaded drawing is rendered as an SVG <image> INSIDE the same viewBox as
 * the geometry, rather than as a separate layer behind the canvas. That is the
 * whole trick: the pixel → metre mapping the analysis already uses is
 *
 *     worldX = (pxX - imageWidth / 2) * scaleFactor
 *
 * so the image occupies world x from -imageWidth·s/2 to +imageWidth·s/2, and y
 * likewise. Placing it at exactly those coordinates makes it land in perfect
 * registration with the detected walls BY CONSTRUCTION — there is no second
 * alignment calculation that could drift out of step with the first, which is
 * the usual way overlays end up subtly offset.
 *
 * It also means the overlay stays aligned as the scale slider moves: both the
 * image rect and the geometry are derived from the same scaleFactor.
 */
function PlanView2D({ plan, label, imageUrl, image, scaleFactor, overlay }) {
  const rooms = plan?.rooms || [];
  const segs = plan?.segments || [];
  if (!segs.length && !rooms.length) return null;

  const xs = segs.flatMap(s => (s.axis === 'v' ? [s.at] : [s.from, s.to]))
    .concat(rooms.flatMap(r => [r.x0, r.x1]));
  const zs = segs.flatMap(s => (s.axis === 'h' ? [s.at] : [s.from, s.to]))
    .concat(rooms.flatMap(r => [r.z0, r.z1]));
  const minX = Math.min(...xs) - PAD, maxX = Math.max(...xs) + PAD;
  const minZ = Math.min(...zs) - PAD, maxZ = Math.max(...zs) + PAD;
  const t = WALL_THICKNESS;

  // The drawing's own extent in world metres.
  const imgW = (image?.width || 0) * scaleFactor;
  const imgH = (image?.height || 0) * scaleFactor;
  const showOverlay = imageUrl && imgW > 0 && imgH > 0 && overlay > 0;

  return (
    <svg
      className="fpi-plan"
      viewBox={`${minX} ${minZ} ${maxX - minX} ${maxZ - minZ}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={label}
    >
      {/* The uploaded plan, underneath everything so the geometry reads on top. */}
      {showOverlay && (
        <image
          href={imageUrl}
          x={-imgW / 2} y={-imgH / 2}
          width={imgW} height={imgH}
          opacity={overlay}
          preserveAspectRatio="none"
        />
      )}

      {/* rooms */}
      {rooms.map((r, i) => (
        <g key={`r${i}`}>
          <rect
            x={Math.min(r.x0, r.x1)} y={Math.min(r.z0, r.z1)}
            width={Math.abs(r.x1 - r.x0)} height={Math.abs(r.z1 - r.z0)}
            className="fpi-room"
          />
        </g>
      ))}

      {/* walls, drawn as bands of the real wall thickness */}
      {segs.map((s, i) => (s.axis === 'h' ? (
        <rect key={`w${i}`} className="fpi-wall"
          x={s.from} y={s.at - t / 2} width={s.to - s.from} height={t} />
      ) : (
        <rect key={`w${i}`} className="fpi-wall"
          x={s.at - t / 2} y={s.from} width={t} height={s.to - s.from} />
      )))}

      {/* openings, over the wall they belong to */}
      {segs.flatMap((s, i) => (s.openings || []).map((o, j) => {
        const cls = o.type === 'window' ? 'fpi-window' : 'fpi-door';
        return s.axis === 'h' ? (
          <rect key={`o${i}_${j}`} className={cls}
            x={o.from} y={s.at - t / 2} width={o.to - o.from} height={t} />
        ) : (
          <rect key={`o${i}_${j}`} className={cls}
            x={s.at - t / 2} y={o.from} width={t} height={o.to - o.from} />
        );
      }))}
    </svg>
  );
}

export default function FloorPlanImport({ onChange, disabled }) {
  const { t } = useT();
  const inputRef = useRef(null);

  const [file, setFile]         = useState(null);
  const [previewUrl, setPreview] = useState('');
  const [detection, setDetection] = useState(null);
  // Wall segments read off the DRAWING (image pixels). Independent of the
  // detector, and the main source of wall geometry — see floorPlanVectors.
  const [imageWalls, setImageWalls] = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [scaleFactor, setScale] = useState(SCALE_DEFAULT);
  // How strongly the uploaded drawing shows through behind the detected
  // geometry. Starts part-way up: the point of the overlay is to CHECK the
  // alignment, so it has to be visible without the user going looking for it.
  const [overlay, setOverlay] = useState(OVERLAY_DEFAULT);

  // One in-flight request at a time; picking a second file cancels the first.
  const abortRef = useRef(null);

  const configured = isRoboflowConfigured();

  // The thumbnail's object URL must be released or every upload leaks a blob.
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);
  useEffect(() => () => abortRef.current?.abort(), []);

  // ── the 3D model ──
  // Rebuilt whenever the detection or the calibration changes. Wall height is
  // NOT scaled: re-calibrating a drawing changes how big the rooms are, not how
  // tall the ceiling is.
  // Plain data — no Three.js objects, so nothing to dispose and no GPU memory
  // to leak as the scale slider re-runs this on every step.
  const plan = useMemo(
    () => (detection ? analysePlan(detection, { scaleFactor, imageWalls }) : null),
    [detection, scaleFactor, imageWalls],
  );

  const stats = plan?.stats;

  // Real-world size of the plan, for the calibration readout.
  const extent = useMemo(() => {
    if (!plan?.segments?.length) return null;
    const xs = plan.segments.flatMap(s => (s.axis === 'v' ? [s.at] : [s.from, s.to]));
    const zs = plan.segments.flatMap(s => (s.axis === 'h' ? [s.at] : [s.from, s.to]));
    return { width: Math.max(...xs) - Math.min(...xs), depth: Math.max(...zs) - Math.min(...zs) };
  }, [plan]);

  // Report upward. Kept in an effect rather than inline in the handlers so the
  // parent always sees the CURRENT scale factor, not the one at upload time.
  //
  // The callback is held in a ref so it is not an effect dependency: parents
  // commonly pass an inline arrow, which would otherwise re-fire this on every
  // parent render and, since the parent stores what it receives, loop.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    onChangeRef.current?.(detection ? { file, detection, scaleFactor, imageWalls } : null);
  }, [file, detection, scaleFactor, imageWalls]);

  const runDetection = useCallback(async (picked) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError('');
    setDetection(null);
    setImageWalls(null);
    try {
      // Read the walls off the drawing and ask Roboflow about rooms and
      // openings at the same time. The extraction is local and usually the
      // faster of the two, so it costs nothing over the network round trip.
      const [walls, result] = await Promise.all([
        loadImage(picked).then(extractWalls).then(r => r.segments).catch((err) => {
          // Never fatal: analysePlan falls back to the detector's room boxes.
          console.warn('[floorplan] wall extraction failed:', err);
          return null;
        }),
        detectFloorPlan(picked, { signal: controller.signal }),
      ]);
      if (controller.signal.aborted) return;
      setImageWalls(walls);
      setDetection(result);
      if (!result.predictions.length) setError(t('fp_nothing_found'));
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error('[floorplan] detection failed:', err);
      setError(err?.message === 'roboflow-not-configured' ? t('fp_not_configured') : t('fp_failed'));
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [t]);

  function handlePick(e) {
    const picked = e.target.files?.[0];
    e.target.value = '';                 // so re-picking the same file re-runs
    if (!picked) return;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(picked);
    setPreview(URL.createObjectURL(picked));
    setScale(SCALE_DEFAULT);
    runDetection(picked);
  }

  function handleClear() {
    abortRef.current?.abort();
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreview('');
    setDetection(null);
    setImageWalls(null);
    setError('');
    setLoading(false);
    setScale(SCALE_DEFAULT);
  }

  return (
    <div className="fpi">
      <p className="fpi-label">{t('fp_label')}</p>

      {!file ? (
        <>
          <button
            type="button"
            className="fpi-drop"
            onClick={() => inputRef.current?.click()}
            disabled={disabled || !configured}
          >
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor"
              strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <span className="fpi-drop-text">{t('fp_upload')}</span>
            <span className="fpi-drop-hint">{t('fp_upload_hint')}</span>
          </button>
          {!configured && <p className="fpi-error">{t('fp_not_configured')}</p>}
        </>
      ) : (
        <div className="fpi-body">
          <div className="fpi-row">
            {previewUrl && <img className="fpi-thumb" src={previewUrl} alt="" />}
            <div className="fpi-meta">
              <span className="fpi-filename">{file.name}</span>
              {loading && (
                <span className="fpi-loading">
                  <span className="fpi-spinner" aria-hidden="true" />
                  {t('fp_detecting')}
                </span>
              )}
              {!loading && stats && (
                <span className="fpi-stats">
                  {t('fp_found')
                    .replace('{rooms}', stats.rooms)
                    .replace('{doors}', stats.doors)
                    .replace('{windows}', stats.windows)}
                </span>
              )}
              {error && <span className="fpi-error">{error}</span>}
            </div>
            <button type="button" className="fpi-clear" onClick={handleClear}
              disabled={disabled} aria-label={t('fp_remove')} title={t('fp_remove')}>✕</button>
          </div>

          {plan && !loading && (
            <>
              <div className="fpi-canvas">
                <PlanView2D
                  plan={plan}
                  label={t('fp_label')}
                  imageUrl={previewUrl}
                  image={detection?.image}
                  scaleFactor={scaleFactor}
                  overlay={overlay}
                />
              </div>

              <div className="fpi-scale">
                <div className="fpi-scale-top">
                  <label className="fpi-scale-label" htmlFor="fpi-scale">{t('fp_scale')}</label>
                  <span className="fpi-scale-value">
                    {extent ? `${extent.width.toFixed(1)} × ${extent.depth.toFixed(1)} m` : ''}
                  </span>
                </div>
                <input
                  id="fpi-scale"
                  className="fpi-slider"
                  type="range"
                  min={SCALE_MIN} max={SCALE_MAX} step={SCALE_STEP}
                  value={scaleFactor}
                  onChange={e => setScale(parseFloat(e.target.value))}
                  disabled={disabled}
                />
                <p className="fpi-scale-hint">{t('fp_scale_hint')}</p>

                <div className="fpi-scale-top fpi-overlay-top">
                  <label className="fpi-scale-label" htmlFor="fpi-overlay">
                    {t('fp_overlay')}
                  </label>
                  <span className="fpi-scale-value">{Math.round(overlay * 100)}%</span>
                </div>
                <input
                  id="fpi-overlay"
                  className="fpi-slider"
                  type="range"
                  min={0} max={1} step={0.01}
                  value={overlay}
                  onChange={e => setOverlay(parseFloat(e.target.value))}
                  disabled={disabled}
                />
                <p className="fpi-scale-hint">{t('fp_overlay_hint')}</p>
              </div>
            </>
          )}
        </div>
      )}

      {/* Shown in BOTH states, deliberately. Before uploading it sets the
          expectation; after uploading it is the reminder to check the result,
          which is the moment it actually matters. */}
      <p className="fpi-notice">
        <span className="fpi-notice-icon" aria-hidden="true">✦</span>
        {t('fp_ai_notice')}
      </p>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handlePick}
        style={{ display: 'none' }}
      />
    </div>
  );
}
